import { useState, useEffect, useMemo, ReactNode } from "react"
import PublicLayout from "@/components/Layout/Public"
import RegistrationForm from "@/components/RegistrationForm"
import ConfirmRegistrationForm from "@/components/ConfirmRegistrationForm"
import LoginForm from "@/components/LoginForm"
import { Link, usePage } from "@inertiajs/react"
import Logo from "@/components/Logo"
import HomeContent from "@/content/Home"
import Alert from "@/components/Alert"
import axios from "axios"
import { chunk } from "lodash"
import angledBg from "@/assets/img/angled-background.svg"
import { Currency } from "@/types/types"
import RichText from "@/components/RichText"
import Footer from "@/components/Footer"

const THRESHOLDS = [0.025, 0.05, 0.1, 0.15, 0.2, 0.25]
const demoPrices = {}
const demoThresholds = {}

const DemoAlert = ({ currency }) => {
  const reference_price =
    demoPrices[currency.symbol] ||
    useMemo(() => {
      let num = (currency.price / 10) * Math.random()
      num *= Math.floor(Math.random() * 2) == 1 ? 1 : -1
      return currency.price - num * Math.random()
    }, [])
  demoPrices[currency.symbol] = reference_price

  const change = currency.price / reference_price - 1
  const change_threshold =
    demoThresholds[currency.symbol] ||
    THRESHOLDS[Math.floor(Math.random() * THRESHOLDS.length)]
  demoThresholds[currency.symbol] = change_threshold

  return (
    <Alert
      className="select-none"
      alert={{
        id: currency.id,
        currency,
        change,
        change_threshold,
        reference_price,
      }}
    />
  )
}

const Register = ({ mode, email, error = [] }) => {
  const { about } = usePage<{
    about: {
      title: string
      body: string
    }
  }>().props
  const [currencies, setCurrencies] = useState<Currency[]>([])

  useEffect(() => {
    const fetchCurrencies = async () => {
      const { data } = await axios.get<Currency[]>("/currencies.json?limit=30")
      setCurrencies([...data].sort(() => Math.random() - Math.random()))
    }
    fetchCurrencies()
  }, [])

  const currencyRows = chunk([...currencies].concat([...currencies]), 3)

  const renderForm = () => {
    switch (mode) {
      case "register":
        return (
          <div>
            <p>
              <span className="mb-1 text-lg font-bold text-white">
                Signup now.{" "}
              </span>
              It’s free!
            </p>

            <div className="mt-4">
              <RegistrationForm email={email} errors={{ email: error }} />
            </div>

            <p className="mt-4 text-sm">
              --
              <br />
              Already got an account?{" "}
              <Link
                href="/login"
                preserveScroll={true}
                preserveState={true}
                className="underline hover:text-white hover:no-underline"
              >
                Log in
              </Link>
            </p>
          </div>
        )
      case "confirm":
        return (
          <div>
            <p>
              <span className="mb-1 text-lg font-bold text-white">
                Almost there!{" "}
              </span>
              Check your email to activate your account.
            </p>
            <div className="mt-4">
              <ConfirmRegistrationForm email={email} errors={{ code: error }} />
            </div>
            <p className="mt-4 text-sm">
              --
              <br />
              Didn't get an email?{" "}
              <Link
                method="post"
                data={{ email }}
                href="/register"
                className="underline hover:text-white hover:no-underline"
              >
                Resend it
              </Link>
            </p>
          </div>
        )
      case "login":
        return (
          <div>
            <p>
              <span className="mb-1 text-lg font-bold text-white">
                Welcome back!{" "}
              </span>
              Enter your email to log in.
            </p>
            <div className="mt-4">
              <LoginForm email={email} />
            </div>
            <p className="mt-4 text-sm">
              --
              <br />
              Need an account?{" "}
              <Link
                href="/register"
                preserveScroll={true}
                preserveState={true}
                className="underline hover:text-white hover:no-underline"
              >
                Sign up
              </Link>
            </p>
          </div>
        )
    }
  }

  return (
    <div>
      <header className="relative">
        <div className="flex flex-col lg:absolute lg:inset-0 lg:flex-row">
          <div
            className="relative z-10 flex-1 w-full -mr-64 bg-gray-800 lg:w-3/5 lg:flex-none lg:bg-transparent"
            style={{
              backgroundImage: `url("${angledBg}")`,
              backgroundSize: "100% auto",
              backgroundPosition: "-5px -5px",
              backgroundRepeat: "no-repeat",
            }}
          >
            <div className="container px-6 pt-12 mx-auto text-center lg:hidden">
              <Logo className="justify-center mb-12 lg:mb-16 lg:justify-start" />
              <HomeContent className="mb-12 lg:mb-16" />
              <div className="w-full max-w-xl p-6 mx-auto -mb-48 text-base text-center text-gray-400 bg-gray-700 rounded-lg">
                {renderForm()}
              </div>
            </div>
          </div>

          <div className="relative z-0 flex-1 h-64 overflow-hidden lg:h-auto">
            <div className="scroll-bg pointer-events-none absolute top-[-15%] left-[-5%] h-[1600px] w-[200%]">
              {currencyRows.map((row, i) => (
                <div
                  key={i}
                  className="flex"
                  style={{
                    paddingLeft: i % 2 === 0 ? "9%" : "0",
                    paddingRight: i % 2 === 1 ? "9%" : "0",
                  }}
                >
                  {row.map((currency) => (
                    <div key={currency.id} className="flex-1 p-2">
                      <DemoAlert currency={currency} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="container relative z-20 hidden max-w-6xl px-6 mx-auto lg:block lg:text-lg">
          <div className="max-w-xl py-16 text-center lg:text-left">
            <Logo className="justify-center mb-12 lg:mb-16 lg:justify-start" />
            <HomeContent className="mb-12 lg:mb-16" />
            <div className="relative z-50 w-full max-w-xl p-6 -mb-32 text-base text-center text-gray-400 bg-gray-700 rounded-lg lg:px-10 lg:py-8 lg:text-left">
              {renderForm()}
            </div>
          </div>
        </div>
      </header>

      <div className="container max-w-6xl px-6 mx-auto">
        <RichText
          className="max-w-2xl mx-auto pt-60 lg:mx-0 lg:pt-32"
          content={about.body}
        />
        <Footer />
      </div>
    </div>
  )
}

Register.layout = (page: ReactNode) => <PublicLayout children={page} />

export default Register
